import React, { useContext, useEffect, useState } from "react";

import "./stylesTransactionsOverview.css";

import Layout from "../../CommonComponents/Layout/Layout";
import { KPI_CARD_BACKGROUND, SIDER_TABS } from "../../../utils/Constants";
import AppContext from "../../../context/AppContext";
import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";

import dayjs from "dayjs";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import DashboardBarChart from "../../CommonComponents/Dashboard/DashboardBarChart/DashboardBarChart";
import DashboardPieChart from "../../CommonComponents/Dashboard/DashboardPieChart/DashboardPieChart";
import GeographicUserInsights from "../../CommonComponents/Dashboard/GeographicUserInsights/GeographicUserInsights";
import { formatNumber } from "chart.js/helpers";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import {
  states,
  getStateNameByAbbreviation,
} from "../../../utils/MapStateName";

const availableColors = [
  "#1ABC9C",
  "#3498DB",
  "#FD6F27",
  "#9B59B6",
  "#6177E8",
  "#E74C3C",
  "#F39C12",
  "#F1C40F",
  "#92929E",
];

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      beginAtZero: true,
      stacked: false,
    },
    y: {
      stacked: false,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    datalabels: {
      display: false,
      anchor: "end",
      align: "start",
      formatter: (value) => value.toLocaleString(),
      color: "black",
      font: {
        family: "Poppins-regular",
        weight: "bold",
      },
    },
    tooltip: {
      enabled: true,
      mode: "index",
      intersect: false,
      backgroundColor: "#252729",
      titleFont: {
        family: "Poppins-regular",
        size: 16,
        weight: "bold",
      },
      bodyFont: {
        family: "Poppins-regular",
        size: 14,
      },
      padding: 15,
      displayColors: true,
      callbacks: {
        label: function (tooltipItem) {
          const label = tooltipItem.dataset.label || "";
          const value = tooltipItem.raw;
          return `${label}: ${formatNumber(value)}`;
        },
      },
    },
  },
};

function TransactionsOverview() {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  const { setActiveSidebarTab } = useContext(AppContext);

  const thisMonthRange = [dayjs().startOf("month"), dayjs().endOf("month")];

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [transactionsOverviewDates, setTransactionsOverviewDates] =
    useState(thisMonthRange);

  const [transactionsOverviewData, setTransactionsOverviewData] = useState({
    deposited: 0,
    depositedPercentageChange: 0,
    redeemable: 0,
    redeemablePercentageChange: 0,
    withdrawn: 0,
    withdrawnPercentageChange: 0,
    revenue: 0,
    revenuePercentageChange: 0,
    push2CardFee: 0,
    push2CardFeePercentageChange: 0,
  });

  const [redeemableCashBarChartData, setRedeemableCashBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Redeemable Cash",
        data: [],
        backgroundColor: ["#1ABC9C"],
        borderColor: ["#1ABC9C"],
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  });
  const [transactionsBarChartData, setTransactionsBarChartData] = useState({
    labels: [],

    datasets: [
      {
        label: "Deposited",
        data: [],
        backgroundColor: ["#6E2EF5"],
        borderColor: ["#6E2EF5"],
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Withdrawn",
        data: [],
        backgroundColor: ["#E6533C"],
        borderColor: ["#E6533C"],
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Revenue",
        data: [],
        backgroundColor: ["#26BF94"],
        borderColor: ["#26BF94"],
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
    headers: [
      { title: "Transactions Volume", amount: 0 },
      { title: "Transactions Count", amount: 0 },
    ],
  });

  const [playersByStatesData, setPlayersByStatesData] = useState([]);
  const [depositsByStatesData, setDepositsByStatesData] = useState([]);
  const [withdrawsByStatesData, setWithdrawsByStatesData] = useState([]);

  const [depositsByPlatformData, setDepositsByPlatformData] = useState([]);
  const [withdrawlsByPlatformData, setWithdrawlsByPlatformData] = useState([]);

  const getTransactionsOverviewData = async (
    queryString = thisMonthRange,
    isQueryString = true
  ) => {
    setIsApiRunning(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    if (startDate === endDate) {
      endDate = dayjs(endDate).add(1, "day").format("YYYY-MM-DD");
    }

    const params = {
      start_date: startDate,
      end_date: endDate,
    };

    try {
      const requests = [
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_WALLET_TRANSACTIONS_OVERVIEW,
          params
        ),
      ];

      const response = await Promise.allSettled(requests);

      const [walletTransactionsOverviewResponse] = response;

      if (walletTransactionsOverviewResponse.status === "fulfilled") {
        const walletTransactions =
          walletTransactionsOverviewResponse?.value?.data?.data;

        setTransactionsOverviewData({
          deposited: walletTransactions?.deposited,
          depositedPercentageChange: walletTransactions?.deposited_pct_change,
          withdrawn: walletTransactions?.withdrawn,
          withdrawnPercentageChange: walletTransactions?.withdrawn_pct_change,
          revenue:
            walletTransactions?.deposited - walletTransactions?.withdrawn,
          revenuePercentageChange:
            ((walletTransactions?.deposited - walletTransactions?.withdrawn) /
              walletTransactions?.deposited) *
            100,
          push2CardFee: walletTransactions?.push2card,
          push2CardFeePercentageChange: walletTransactions?.push2card_pct_change,
        });

        const redeemableCashPlot = walletTransactions?.redeemable_cash_plot;
        const redeemableCashDatasets = redeemableCashPlot?.buckets?.map(
          (bucket) => bucket.value
        );
        const redeemableCashLabels = redeemableCashPlot?.buckets?.map(
          (bucket) => bucket.label
        );

        const transactionsVolumePlot =
          walletTransactions?.transaction_volume_plot;
        const transactionsVolumeDeposits = transactionsVolumePlot?.buckets?.map(
          (bucket) => bucket.deposits
        );
        const transactionsVolumeWithdrawals =
          transactionsVolumePlot?.buckets?.map((bucket) => bucket.withdrawals);
        const transactionsVolumeRevenue = transactionsVolumePlot?.buckets?.map(
          (bucket, index) => {
            const revenue =
              transactionsVolumeDeposits[index] -
              transactionsVolumeWithdrawals[index];
            return revenue;
          }
        );

        const transactionsVolumeLabels = transactionsVolumePlot?.buckets?.map(
          (bucket) => bucket.label
        );

        const transactionMapPlot = walletTransactions?.transaction_map_plot;
        const transactionMapData = transactionMapPlot?.map((item) => {
          return {
            name: getStateNameByAbbreviation(Object.keys(item)[0]),
            abbr: Object.keys(item)[0],
            amount: item[Object.keys(item)[0]].total_volume || 0,
          };
        });

        const uniqueUsersByStatePlot =
          walletTransactions?.players_by_state_plot;

        const uniqueUsersByStateData = uniqueUsersByStatePlot?.map((item) => {
          return {
            name: getStateNameByAbbreviation(item.state),
            abbr: item.state,
            amount: item.unique_users || 0,
          };
        });

        const withdrawalsByStatePlot =
          walletTransactions?.withdrawals_by_state_plot;
        const withdrawalsByStateData = withdrawalsByStatePlot?.map((item) => {
          return {
            name: getStateNameByAbbreviation(item.state),
            abbr: item.state,
            amount: item.total_withdrawals || 0,
          };
        });

        const depositsByPlatformPlot =
          walletTransactions?.deposits_by_platform_plot;
        const depositsByPlatformData = depositsByPlatformPlot?.map(
          (item, index) => {
            return {
              device: item.platform,
              count: item.total_deposits,
              color: availableColors[index],
            };
          }
        );

        const withdrawalsByPlatformPlot =
          walletTransactions?.withdrawals_by_platform_plot;
        const withdrawalsByPlatformData = withdrawalsByPlatformPlot?.map(
          (item, index) => {
            return {
              device: item.platform,
              count: item.total_withdrawals,
              color: availableColors[index],
            };
          }
        );

        setRedeemableCashBarChartData((prev) => ({
          labels: redeemableCashLabels,
          datasets: [
            {
              ...prev.datasets[0],
              data: redeemableCashDatasets,
            },
          ],
        }));

        setTransactionsBarChartData((prev) => ({
          labels: transactionsVolumeLabels,
          datasets: [
            { ...prev.datasets[0], data: transactionsVolumeDeposits },
            { ...prev.datasets[1], data: transactionsVolumeWithdrawals },
            { ...prev.datasets[2], data: transactionsVolumeRevenue },
          ],
          headers: [
            {
              title: "Transactions Volume",
              amount: transactionsVolumePlot?.transaction_volume,
            },
            {
              title: "Transactions Count",
              amount: transactionsVolumePlot?.transaction_count,
            },
          ],
        }));

        setPlayersByStatesData(uniqueUsersByStateData);
        setDepositsByStatesData(transactionMapData);
        setWithdrawsByStatesData(withdrawalsByStateData);

        setDepositsByPlatformData(depositsByPlatformData);
        setWithdrawlsByPlatformData(withdrawalsByPlatformData);
      }
    } catch (error) {
      console.log("Error fetching wallet transactions", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.TRANSACTIONS, "Overview");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    getTransactionsOverviewData(thisMonthRange, false);
  }, []);

  return (
    <Layout name={SIDER_TABS.TRANSACTIONS}>
      <div className="transactions-overview-container">
        <div className="transactions-overview-header">
          <h2 className="transactions-overview-title">Overview</h2>
          <CustomDatePicker
            getData={getTransactionsOverviewData}
            dates={transactionsOverviewDates}
            setDates={setTransactionsOverviewDates}
            showPresets={true}
            startRange="This Week"
          />
        </div>

        <div className="transactions-overview-content">
          <div className="transactions-overview-top-content">
            <div className="transactions-overview-top-content-left">
              <div className="transactions-overview-top-content-left-row">
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="DEPOSITED"
                  value={transactionsOverviewData.deposited}
                  hugeValue={false}
                  percentage={
                    transactionsOverviewData.depositedPercentageChange
                  }
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText=""
                />
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="WITHDRAWN"
                  value={transactionsOverviewData.withdrawn}
                  hugeValue={false}
                  percentage={
                    transactionsOverviewData.withdrawnPercentageChange
                  }
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText=""
                />
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="REVENUE"
                  value={transactionsOverviewData.revenue}
                  hugeValue={false}
                  percentage={false}
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText=""
                />
              </div>
              <div className="transactions-overview-top-content-left-row height-490">
                <DashboardBarChart
                  title="Redeemable Cash"
                  data={redeemableCashBarChartData}
                  isApiRunning={isApiRunning}
                />
              </div>
              {/* <div className="transactions-overview-top-content-left-row">
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="POTENTIAL FRAUD"
                  value={transactionsOverviewData.potentialFraud || 0}
                  hugeValue={false}
                  percentage={
                    transactionsOverviewData.potentialFraudPercentageChange || 0
                  }
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText="vs yesterday"
                />
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="REJECTED AUTOMATICALLY"
                  value={112036}
                  hugeValue={false}
                  percentage={-54}
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText="vs yesterday"
                />
              </div> */}
            </div>
            <div className="transactions-overview-top-content-right">
              <div className="transactions-overview-top-content-right-row">
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="Push2Card 1% Fee"
                  value={transactionsOverviewData.push2CardFee}
                  hugeValue={false}
                  percentage={
                    transactionsOverviewData.push2CardFeePercentageChange
                  }
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText=""
                />
              </div>
              <div className="transactions-overview-top-content-right-row">
                <div
                  className="transactions-overview-geo-content height-490"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    flex: "auto",
                  }}
                >
                  <GeographicUserInsights
                    title="Players by States"
                    data={playersByStatesData}
                    isApiRunning={isApiRunning}
                  />
                </div>
              </div>
              {/* <div className="transactions-overview-top-content-right-row">
                <KpiCard
                  isApiRunning={isApiRunning}
                  title="REJECTED BY ADMIN"
                  value={130841}
                  hugeValue={false}
                  percentage={46}
                  backgroundColor={KPI_CARD_BACKGROUND.LIGHT}
                  customText="vs yesterday"
                />
              </div> */}
            </div>
          </div>
          <div className="transactions-overview-content-row">
            <DashboardBarChart
              data={transactionsBarChartData}
              showCustomDatePicker={false}
              headers={transactionsBarChartData.headers}
              isApiRunning={isApiRunning}
              setSelected={() => {}}
              customOptions={options}
            />
          </div>
          <div className="transactions-overview-content-row">
            <div className="transactions-overview-geo-content">
              <GeographicUserInsights
                title="Deposit by States"
                data={depositsByStatesData}
                isDollars={true}
                isApiRunning={isApiRunning}
              />
            </div>
            <div className="transactions-overview-geo-content">
              <GeographicUserInsights
                title="Withdraw by States"
                data={withdrawsByStatesData}
                isDollars={true}
                isApiRunning={isApiRunning}
              />
            </div>
            {/* <div className="transactions-overview-geo-content">
              <GeographicUserInsights title="Fraud by States" />
            </div> */}
          </div>
          <div className="transactions-overview-content-row">
            <div className="transactions-overview-pie-chart-container">
              <DashboardPieChart
                title="Deposit by Platform"
                pieData={depositsByPlatformData}
                isApiRunning={isApiRunning}
                isDollars={true}
              />
            </div>
            {/* <div className="transactions-overview-pie-chart-container">
              <DashboardPieChart
                title="Withdraw by Platform"
                pieData={withdrawlsByPlatformData}
                isApiRunning={isApiRunning}
                isDollars={true}
              />
            </div> */}
            {/* <DashboardPieChart
              title="Fraud by Platform"
              pieData={pieData}
              isApiRunning={isApiRunning}
            /> */}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default TransactionsOverview;
